
import {
  Component,
  Mixins,
  Provide,
  Ref,
  ProvideReactive,
  Watch
} from "vue-property-decorator";
import {
  ConsoleLogger,
  DefaultDeviceController,
  LogLevel,
  VideoTileState,
  DeviceController,
  MeetingSessionConfiguration,
  DefaultMeetingSession,
  MeetingSession,
  AudioVideoFacade,
  ContentShareObserver,
  VideoInputDevice,
  BackgroundBlurVideoFrameProcessor
} from "amazon-chime-sdk-js";
import { AxiosInstance } from "axios";
import { throttle, findKey, debounce } from "lodash";
import Pusher from "pusher-js";

import {
  Flows,
  DetailsResponse,
  Action,
  BackendMember,
  GiptContextProperty,
  MoreDetailsResponseSetting,
  ExtraButton,
  Slide
} from "@/interfaces/meeting/details";
import {
  LocalUserId,
  LocalUserState,
  Roster,
  RosterKey,
  getBlankState
} from "@/interfaces/meeting/roster";
import { DeviceSelection } from "@/interfaces/devices/selection";
import UserDetailsMixin from "@/mixins/UserDetailsMixin.vue";
import ChatMixin from "@/mixins/ChatMixin.vue";
import SignatureMixin from "@/mixins/SignatureMixin.vue";
import { connect } from "@/services/socket/pusher";
import { attendMeeting, updateName, getNames } from "@/services/api/meeting";
import { setClientAuthentication } from "@/services/api/client";
import {
  setChannels,
  setClient,
  sendNameUpdate
} from "@/services/meeting/messaging";
import { storeDevices } from "@/services/devices/storage";
import { QualityMappings } from "@/services/chime/qualityMappings";
import { JsonConsoleLogger } from "@/services/logging/consoleLogger";
import { setupObservers, EventTypes } from "@/services/socket/meeting";
import { storeName } from "@/services/meeting/storage";
import {
  isAttendeeIdForContent,
  attendeeIdWithoutContent
} from "@/services/meeting/roster";
import {
  AudioVideoKey,
  MeetingSessionKey
} from "@/services/meeting/audioVideoObserver";
import { getVideoInputDevice } from "@/utils/device";
import { getMeetingDetails } from "@/utils/meeting";
import { findById } from "@/utils/controls";

const MAX_ALLOWED_INACTIVE_MINUTES = 120;

@Component
export default class DashboardMixin
  extends Mixins<UserDetailsMixin, ChatMixin, SignatureMixin>(
    UserDetailsMixin,
    ChatMixin,
    SignatureMixin
  )
  implements ContentShareObserver {
  apiClient!: AxiosInstance;
  pusherClient!: Pusher;
  @ProvideReactive(MeetingSessionKey) meetingSession!: MeetingSession;
  @ProvideReactive(AudioVideoKey) audioVideo!: AudioVideoFacade;
  deviceController!: DeviceController;
  serverDetails!: DetailsResponse & MoreDetailsResponseSetting;
  isMicrophoneOn = true;
  isCameraOn = true;
  isCameraAvailable = true;
  isBlurVideo = false;
  deviceSelection!: DeviceSelection;
  tNc = "";
  eventLanguages = ["en"];
  programName = "";
  eventId = 0;
  eventColor = "";
  additionalField = "";
  currency = "";
  eventLogo = "";
  taxMessage = "";
  numberFormat = "";
  isLive = false;
  isActionNav = true;
  isDrawerVisible = true;
  windowWidth = 0;
  hasDidNotAttendButton = false;
  hasReserveButton = false;
  hasBlurVideoFeature = false;
  giptSchema: object = {};
  giptContextProperties: GiptContextProperty[] = [];
  activationType = "NONE";
  navigatingPopupSizeInPx = 900;
  bookingSectionAboveClient = false;
  skipSlideChangeOnMeetingStart = false;

  controls!: Action[];
  flow!: Flows;
  loadingText = "";
  miniVariant = true;
  meetingStartTime = new Date();
  // The duration a meeting has only one participant in the roster
  inactiveMinutes = 0;
  timeoutCounter!: NodeJS.Timeout | null;
  slide!: Slide;

  @ProvideReactive(RosterKey) roster: Roster = {};
  isDisplayDialog!: boolean;

  @Provide() logger = new JsonConsoleLogger(
    "The Gary",
    process.env.NODE_ENV === "development" ? LogLevel.DEBUG : LogLevel.INFO
  );

  @Provide() readonly chimeLogger = new ConsoleLogger(
    "BOOST_NOW",
    process.env.NODE_ENV === "development" ? LogLevel.ERROR : LogLevel.ERROR
  );

  @Ref() audioElement!: HTMLAudioElement;

  destroyed() {
    if (this.timeoutCounter) {
      clearTimeout(this.timeoutCounter);
    }
  }

  async created() {
    const params = new URLSearchParams(window.location.search);
    this.isLive = params.get("isLive") === "true";
    if (this.isLive) {
      this.onScreenResize();
      window.addEventListener("resize", this.onScreenResize);
    }

    this.logger.info(
      `[VERSION] Working on version ${process.env.VUE_APP_GIT_HASH}`
    );
    this.logger.debug("[MIXINS] Calling created from DashboardMixin");
    const token = params.get("token") ?? "";
    this.apiClient = setClientAuthentication(token);

    this.deviceController = new DefaultDeviceController(this.chimeLogger);

    this.loadingText = this.$t("Loading details of meeting with PersonName", {
      PersonName: this.detailsUserName
    }).toString();

    this.logger.debug(
      "[MIXINS] Created from dashboard mixin just became asynchronous"
    );

    // Need to wait for meeting details and creation from backend
    try {
      this.serverDetails = await getMeetingDetails(this.apiClient);
    } catch (ex) {
      this.logger.error("[DETAILS] Get details failed", ex);
    }

    if (this.serverDetails) {
      // Set up communications channels
      setChannels(
        this.serverDetails.lead_channel,
        this.serverDetails.lead_boost_now_channel
      );
      this.controls = this.serverDetails.actions;

      if (this.isLive) {
        const welcomeloungeString = "welcome lounge";
        const mainchatString = "main chat";
        const internalString = "*internal*";

        this.controls.map(slideTree1 => {
          slideTree1.children?.map((slideTree2, key) => {
            const slideName = slideTree2.name.toLowerCase();
            if (
              slideName.includes(internalString) ||
              slideName.toLowerCase().includes(welcomeloungeString) ||
              slideName.toLowerCase().includes(mainchatString)
            ) {
              slideTree1.children?.splice(key, 1);
            }
            slideTree2.children?.map((slideTree3, key) => {
              if (slideTree3.name.toLowerCase().includes(internalString)) {
                slideTree2.children?.splice(key, 1);
              }
            });
          });
        });
      }

      this.loadingText = this.$t("Connecting sockets").toString();
      this.pusherClient = connect(
        this.serverDetails.pusher_key,
        this.serverDetails.pusher_cluster,
        token
      );
      // IMPORTANT NOTE: due to serverDetails not being initialized, they are not responsive; no computed prop can rely on their value changing hence the below where we set all sorts of props
      // TODO: About the note above: Why don't we initialize serverDetails in the constructor then??
      this.activationType = this.serverDetails.activation_meeting || "NONE";
      this.tNc = this.serverDetails.tnc;
      this.programName = this.serverDetails.program_name;
      this.currency = this.serverDetails.currency;
      this.eventId = this.serverDetails.event_id;
      this.eventColor = this.serverDetails.event_color;
      this.additionalField = this.serverDetails.additional_field;
      this.numberFormat = this.serverDetails.number_format;
      this.eventLogo = this.serverDetails.event_logo_url || "";
      this.taxMessage = this.serverDetails.tax_message || "";
      this.navigatingPopupSizeInPx = this.serverDetails.BOOST_NOW_navigating_popup_size_in_px || 900;
      this.skipSlideChangeOnMeetingStart = Boolean(this.serverDetails.BOOST_NOW_skip_slide_change_on_meeting_start);
      this.bookingSectionAboveClient = Boolean(this.serverDetails.BOOST_NOW_booking_section_above_client);
      this.hasReserveButton = this.serverDetails.has_reserve_button
        ? true
        : false;
      this.hasDidNotAttendButton = this.serverDetails.has_did_not_attend_button
        ? true
        : false;
      // Note: Apparently events can decide to entirely disable the blur video feature. If it is not the case then we check with the browser
      this.hasBlurVideoFeature = this.serverDetails.has_blur_video_feature
        ? await BackgroundBlurVideoFrameProcessor.isSupported()
        : false;
      this.giptSchema = this.serverDetails.gipt_schema;
      this.giptContextProperties = this.serverDetails.gipt_context_properties;
      this.pusherClient.subscribe(this.serverDetails.lead_channel);
      this.pusherClient.subscribe(this.serverDetails.lead_boost_now_channel);
      setClient(this.pusherClient);
      setupObservers(this.pusherClient, {
        [EventTypes.NAME_UPDATE]: this.loadNames.bind(this),
        [EventTypes.UPDATE_CHAT]: (value: { sender: string }) =>
          this.loadChats(value, this.logger, this.apiClient, this.miniVariant)
      });
      this.loadingText = "Miscounting rivets";
      setTimeout(() => (this.flow = Flows.DeviceSelection), 200);
      this.setupObservers();
      this.setupInactiveSessionChecker();
      // Handle languages
      if (this.serverDetails.BOOST_NOW_languages && this.serverDetails.BOOST_NOW_languages.length > 0) {
        this.eventLanguages = [...this.serverDetails.BOOST_NOW_languages, "en"];
      }
      // Any event with languages support
      if ([184].includes(this.eventId)) {
        // Check whether language is saved inside localStorage
        let lang = localStorage.getItem(`boost_${this.eventId}_language`);
        if (!lang) {
          const defaultLanguages: Record<string, string> = {
            "184": "ar"
          };
          lang = defaultLanguages[this.eventId.toString()];
        }
        // Fallback in case we really can't figure out the language
        if (!lang) {
          lang = "en";
        }
        this.$i18n.locale = lang;
      }
    }
  }

  setupInactiveSessionChecker() {
    setInterval(() => {
      // Update inactive minutes
      if (this.rosterSize == 1) {
        this.inactiveMinutes++;
      } else if (this.rosterSize > 1) {
        this.inactiveMinutes = 0;
      }
      this.logger.debug(
        `Roster size: ${this.rosterSize}, Inactive minutes: ${this.inactiveMinutes}`
      );
      // Check whether has reached maximum inactive minutes
      if (this.inactiveMinutes >= MAX_ALLOWED_INACTIVE_MINUTES) {
        this.inactiveMinutes = 0;
        this.audioVideo.stopLocalVideoTile();
        this.audioVideo.stop();
        window.location.replace("meetingEnded.html");
      }
    }, 60 * 1000);
  }

  onScreenResize() {
    this.windowWidth = window.innerWidth;
    this.isActionNav = this.windowWidth > 1024;
    this.isDrawerVisible = this.windowWidth > 1024;
  }

  setupObservers() {
    throw new Error("Method not implemented.");
  }

  async loadNames({ sender }: { sender: string }) {
    this.logger.debug(
      "[ROSTER] Names were updated, will load roster from server. Update was sent by ",
      sender
    );
    const { roster } = await getNames(this.apiClient);
    // Might not have roster yet on backend
    if (roster) {
      this.updateRosterNames(roster);
    }
  }

  onToggleMicrophone(mute?: boolean) {
    this.logger.debug(`Toggle microphone triggered.`);
    let current = this.audioVideo.realtimeIsLocalAudioMuted();
    // Forces a "set" behavior rather than toggle if need be
    if (mute !== undefined) {
      current = !mute;
    }
    this.logger.debug(`Currently muted status is ${current}`);
    if (current) {
      this.turnMicrophoneOn();
    } else {
      this.turnMicrophoneOff();
    }
  }

  onToggleBlur(on: boolean) {
    this.isBlurVideo = on;
    if (this.isCameraOn) {
      this.turnCameraOn();
    }
  }

  turnMicrophoneOff() {
    this.audioVideo.realtimeMuteLocalAudio();
    this.isMicrophoneOn = false;
    this.logger.debug("[MICROPHONE] Microphone turned OFF");
  }

  turnMicrophoneOn() {
    this.audioVideo.realtimeUnmuteLocalAudio();
    this.isMicrophoneOn = true;
    this.logger.debug("[MICROPHONE] Microphone turned ON");
  }

  async onToggleCamera() {
    this.logger.debug(`[CAMERA] Toggle camera triggered.`);
    const current = Boolean(
      this.audioVideo.getLocalVideoTile()?.state()?.active
    );
    this.logger.debug(`[CAMERA] Currently camera status is ${current}`);
    // Note, due to the await, isCameraOn toggle needs to be within if/else
    if (current) {
      this.turnCameraOff();
    } else {
      this.turnCameraOn();
    }
  }

  async turnCameraOff() {
    await this.deviceController.chooseVideoInputDevice(null);
    const outcome = this.audioVideo.stopLocalVideoTile();

    this.logger.debug(
      "[CAMERA] Outcome of audioVideo s stopLocalVideoTile: ",
      outcome
    );
    this.isCameraOn = false;
  }

  async turnCameraOn() {
    const videoInputDevice: VideoInputDevice | null = await getVideoInputDevice(
      this.deviceSelection.videoInput,
      this.isBlurVideo,
      this.chimeLogger
    );
    await this.deviceController.chooseVideoInputDevice(videoInputDevice);
    const outcome = this.audioVideo.startLocalVideoTile();
    this.logger.debug(
      "[CAMERA] Outcome of audioVideo s startLocalVideoTile toggle: ",
      outcome
    );
    this.isCameraOn = true;
  }

  updateRosterValue(
    id: LocalUserId,
    fn: (member: LocalUserState) => void,
    createIfMissing = true
  ) {
    let member = this.roster[id];
    if (!member && createIfMissing) {
      member = getBlankState();
      this.$set(this.roster, id, member);
    }
    fn(member);
    this.logger.debug("[ROSTER] After update, roster was", this.roster);
  }

  async joinMeeting([name, selection, isBlurVideo]: [
    string,
    DeviceSelection,
    boolean
  ]) {
    this.isBlurVideo = isBlurVideo;
    this.flow = Flows.Loading;
    await storeDevices(selection);
    this.deviceSelection = selection;
    // Update isCameraAvailable
    this.isCameraAvailable = selection.videoInput !== null;
    this.setupInitialMeetingLoadingText();
    const { meeting, attendee } = await attendMeeting(this.apiClient);
    // If name value from devices selector is not empty, update name
    this.updateName(name);
    // if (name) {
    // }
    const config = new MeetingSessionConfiguration(
      { Meeting: meeting },
      { Attendee: attendee }
    );
    this.loadingText = this.$t("Joining Chime meeting").toString();
    this.meetingSession = new DefaultMeetingSession(
      config,
      this.chimeLogger,
      this.deviceController as DefaultDeviceController
    );
    this.audioVideo = this.meetingSession.audioVideo;
    // Set video/audio on device controller
    // User might have opted out of video
    if (selection.isVideoOn) {
      await this.turnCameraOn();
    } else {
      await this.turnCameraOff();
    }
    try {
      await this.deviceController.chooseAudioInputDevice(selection.audioInput);
    } catch (e) {
      this.logger.error("chooseAudioInputDevice error: ", e);
    }
    // Off the microphone if selected to do so
    if (!selection.isMicrophoneOn) {
      this.turnMicrophoneOff();
    }
    // Firefox doesnt have that value
    try {
      await this.deviceController.chooseAudioOutputDevice(
        selection.audioOutput?.deviceId
      );
    } catch (e) {
      this.logger.error("chooseAudioOutputDevice error: ", e);
      window.alert(
        this.$t("Audio output device not supported on this browser. Please use Chrome or Edge.").toString()
      );
    }
    this.deviceController.chooseVideoInputQuality(
      ...QualityMappings.get(selection.videoQuality)
    );
    this.audioVideo.start();
    // Little helper for debug mode to access the audioVideo object
    process.env.NODE_ENV === "development"
      ? ((window as any).audioVideo = this.audioVideo)
      : null;
    this.setupMeetingObservers();
    this.logger.debug("Attempting to bind audio element", this.audioElement);
    const audioBindingReturn = this.audioVideo.bindAudioElement(
      this.audioElement
    );
    this.logger.debug("Return value from audio binding: ", audioBindingReturn);
    if (selection.isVideoOn) {
      this.audioVideo.startLocalVideoTile();
    }
    this.flow = Flows.InVideo;
    this.showNavigating = true;
  }

  setupMeetingObservers() {
    const executeTimer = debounce(this.executeClientTimer, 2000);
    this.audioVideo.addObserver(this);
    this.audioVideo.addContentShareObserver(this);
    this.audioVideo.realtimeSubscribeToAttendeeIdPresence(
      (
        attendeeId: string,
        present: boolean,
        externalUserId: string | null | undefined
      ) => {
        this.logger.debug(
          "[PRESENCE] Received real time presence",
          attendeeId,
          present,
          externalUserId
        );
        // As long as we have an external user id, we should record
        if (externalUserId !== null && externalUserId !== undefined) {
          this.logger.debug(
            "[PRESENCE] Processing real time presence of user ",
            externalUserId
          );
          if (present) {
            // content share comes with an appended #content, remove it on presence
            attendeeId = attendeeIdWithoutContent(attendeeId);
            this.logger.debug(
              "[PRESENCE] Presence recorded for attendee",
              attendeeId,
              externalUserId
            );
            this.updateRosterValue(
              externalUserId,
              member => (member.attendeeId = attendeeId)
            );
            // Load the names
            this.loadNames({ sender: "myself" });
            this.logger.debug("[PRESENCE] Roster is now ", this.roster);
            this.logger.debug(
              "[PRESENCE] Registering volume indicator observer for user",
              externalUserId
            );
            // Let's make sure we avoid double subscriptions by unsubscribing (?)
            this.audioVideo.realtimeUnsubscribeFromVolumeIndicator(attendeeId);
            this.audioVideo.realtimeSubscribeToVolumeIndicator(
              attendeeId,
              this.onVolumeIndicator.bind(this)
            );
          } else {
            if (isAttendeeIdForContent(attendeeId)) {
              this.logger.debug(
                "[PRESENCE][ABSENCE] Absence is for content share; not recording it",
                attendeeId,
                externalUserId
              );
            } else {
              this.logger.debug(
                "[PRESENCE][ABSENCE] Attendee not present ",
                attendeeId,
                externalUserId
              );
              this.audioVideo.realtimeUnsubscribeFromVolumeIndicator(
                attendeeId
              );
              this.$delete(this.roster, externalUserId);
              this.logger.debug(
                "[PRESENCE][ABSENCE] After absence recorded, roster is",
                this.roster
              );
            }
          }
        } else {
          this.logger.warn(
            "[PRESENCE] Received presence with empty local user id"
          );
        }

        // during first join, client will only have themselves in the roster
        executeTimer();

        // if available
        // this.executeClientTimer();
      }
    );
    // First load of the chats.
    this.loadChats({ sender: "" }, this.logger, this.apiClient, false);
  }

  assignTile(userId: string, tile: VideoTileState) {
    this.logger.debug("[PRESENCE][TILES] Assigning ", userId, "to", tile);
    if (tile) {
      this.updateRosterValue(userId, member => {
        const tileId = tile.tileId || 0;
        if (tile.isContent) {
          this.logger.debug("[TILES] Tile is content share ", tile);
          member.contentTileId = tileId;
        } else {
          member.tileId = tileId;
          member.cameraOn = tile.active;
        }
      });
      this.logger.debug(
        "[ROSTER][TILES][PRESENCE] After update, roster is ",
        this.roster
      );
    }
  }

  setupInitialMeetingLoadingText() {
    throw new Error("Method not implemented.");
  }

  updateRosterNames(roster: BackendMember[]) {
    // Some members might not exist (yet or anymore) hence the check on "member"
    roster.forEach(backendMember =>
      this.updateRosterValue(
        backendMember.user_id,
        member => (member ? (member.name = backendMember.name) : null),
        false
      )
    );
  }

  executeClientTimer() {
    this.logger.debug("[CLIENT][TIMER] starts");
    const timer = this.serverDetails?.BOOST_NOW_sales_rep_timer;
    if (timer && this.detailsUserId.toLowerCase().includes("client")) {
      const currentRoles = Object.keys(this.roster);
      const salesRepLoggedIn = currentRoles.find(role => role === "salesrep");
      this.logger.debug(
        `[CLIENT][TIMER] sales rep logged in, ${salesRepLoggedIn}`
      );
      this.logger.debug(`[CLIENT][TIMER] current roles, ${currentRoles}`);

      if (
        !this.timeoutCounter &&
        !salesRepLoggedIn &&
        (!this.slide || this.slide?.id !== timer.actionId)
      ) {
        // check if roster has any client
        const found = currentRoles.find(
          role =>
            role.toLowerCase().includes("client") && role !== this.detailsUserId
        );
        this.logger.debug(`[CLIENT][TIMER] client found, ${found}`);
        if (!found) {
          // start countdown
          this.timeoutCounter = setTimeout(() => {
            const control = findById(this.controls, timer.actionId);
            if (control) this.recordMeetingState(control);
          }, timer.timeInSeconds * 1e3);
        }
      } else if (salesRepLoggedIn && this.timeoutCounter) {
        clearTimeout(this.timeoutCounter);
      }
    }
  }

  async recordMeetingState(action: Action) {
    throw new Error("Method not implemented.");
  }

  async contentShareDidStop(): Promise<void> {
    // TODO implement here or in each project?
    this.logger.debug("[TILES][CONTENT] Content share stopped");
  }

  async contentShareDidStart(): Promise<void> {
    this.logger.debug("[TILES][CONTENT] Content share started");
  }

  async contentShareDidPause(): Promise<void> {
    this.logger.debug("[TILES][CONTENT] Content share paused");
  }

  async contentShareDidUnpause(): Promise<void> {
    this.logger.debug("[TILES][CONTENT] Content share unpaused");
  }

  async updateName(name: string) {
    // Save to localStorage for reuse (reps mainly)
    storeName(name);
    this.details.name = name;
    const { roster } = await updateName(this.apiClient, {
      userId: this.detailsUserId,
      name
    });
    this.updateRosterNames(roster);
    // Inform all that something has changed
    sendNameUpdate(this.detailsUserId);
    this.isDisplayDialog = false;
  }

  // START - AudioVideoObserver methods
  audioVideoDidStart(): void {
    this.logger.debug("Video did start");
  }

  videoTileDidUpdate(tileState: VideoTileState) {
    this.logger.debug("[TILES] Tile received ", tileState);
    const localUserId = tileState.boundExternalUserId;
    if (localUserId) {
      this.logger.debug("[TILES] Roster id is ", localUserId);
      this.logger.debug(
        "[TILES][ROSTER] Current local state for roster is ",
        this.roster[localUserId]
      );
      // See if this tile corresponds to a present user
      if (localUserId in this.roster) {
        this.logger.debug(
          "[TILES] Found user corresponding to roster ",
          localUserId
        );
      } else {
        this.logger.debug(
          "[TILES] No user corresponding to roster. Looked inside",
          this.roster
        );
      }
      // Regardless, we assign to local user
      this.assignTile(localUserId, tileState);
    } else {
      this.logger.debug(
        "[TILES] No roster id found on tile. Not doing anything"
      );
    }
  }

  videoTileWasRemoved(tileId: number): void {
    this.logger.debug(`[TILES] Tile was removed ${tileId}`);
    const localUserId = findKey(this.roster, member =>
      [member.tileId, member.contentTileId].includes(tileId)
    );
    if (localUserId) {
      this.logger.debug("[TILES] Roster id of removed tile is ", localUserId);
      this.updateRosterValue(
        localUserId,
        member => {
          if (member.tileId === tileId) {
            member.tileId = 0;
          } else if (member.contentTileId === tileId) {
            member.contentTileId = 0;
          }
        },
        false
      );
    } else {
      this.logger.debug("[TILES] Removed tile was not assigned to roster");
    }
  }
  // END - AudioVideoObserver methods

  // START - Volume indicator with throttle
  onVolumeIndicator(
    attendeeId: string,
    volume: number | null,
    muted: boolean | null,
    signalStrength: number | null,
    externalUserId?: string | null | undefined
  ) {
    if (externalUserId) {
      if (muted !== null) {
        this.logger.debug(
          `[ROSTER][VOLUME][MUTE] Received muted signal for roster ${externalUserId}. Setting muted to ${muted}`
        );
        this.updateRosterValue(
          externalUserId,
          member => (member.muted = muted)
        );
      }
    }
    this.throttledOnVolumeIndicator(
      attendeeId,
      volume,
      muted,
      signalStrength,
      externalUserId
    );
  }

  throttledOnVolumeIndicator = throttle(
    (
      attendeeId: string,
      volume: number | null,
      muted: boolean | null,
      signalStrength: number | null,
      externalUserId?: string | null | undefined
    ) => {
      this.logger.debug("[ROSTER][VOLUME]", externalUserId, volume, muted);
      if (externalUserId) {
        if (externalUserId in this.roster) {
          // Update only down to a decimal place
          this.updateRosterValue(
            externalUserId,
            member => (member.volume = Math.floor((volume || 0) * 100)),
            false
          );
        }
      }
    },
    100
  );
  // END - Volume indicator with throttle

  // START - Check flow
  get isInDeviceSelectionFlow() {
    return Flows.DeviceSelection === this.flow;
  }

  get isInLoadingFlow() {
    return Flows.Loading === this.flow;
  }

  get isInInVideoFlow() {
    return Flows.InVideo === this.flow;
  }
  // END - Check flow

  get rosterSize() {
    return Object.keys(this.roster).length;
  }

  get rosterUserName() {
    return this.roster[this.detailsUserId]?.name || "";
  }

  get userName() {
    return this.rosterUserName || this.detailsUserName || "";
  }

  get userNameOrId() {
    return this.userName || this.detailsUserId;
  }

  get signOnly() {
    return this.serverDetails.BOOST_NOW_sign_only ?? false;
  }

  get autoTNC() {
    return this.serverDetails.BOOST_NOW_auto_tnc ?? false;
  }

  get extraButtons() {
    return this.serverDetails.BOOST_NOW_extra_buttons ?? [];
  }

  get hasConfirmButton() {
    return this.serverDetails.BOOST_NOW_has_confirm_button ?? true;
  }

  get currentLanguage() {
    return this.$i18n.locale || "en";
  }

  get otherLanguage() {
    if (this.eventLanguages.length === 1) {
      return "";
    } else {
      return this.eventLanguages.filter(
        lang => lang !== this.currentLanguage
      )[0];
    }
  }

  changeToOtherLanguage() {
    this.$i18n.locale = this.otherLanguage;
  }

  @Watch("currentLanguage")
  onCurrentLanguageChange(v: string) {
    localStorage.setItem(`boost_${this.eventId}_language`, v);
    this.$vuetify.lang.current = this.otherLanguage;
    const ltrRtl = this.$t("dir").toString();
    document.documentElement.setAttribute("dir", ltrRtl);
    this.$vuetify.rtl = ltrRtl === "rtl";
  }
}
